import {parseInteger} from '@benchling/js-utils';
import PropTypes from 'prop-types';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import * as Util from 'notebook/tables/excess/Util';
import FormGroup from 'ui-library/bs/FormGroup';
import FormInput from 'ui-library/bs/FormInput';
import Label from 'ui-library/bs/Label';
import {ButtonStyle} from 'ui-library/buttons/Button';
import ModalAnchor from 'ui-library/ModalAnchor';
import Modal from 'ui-library/modals/Modal';
import {InputChangeEvent, Optional} from 'util/Types';

const {MIN_PRECISION, MAX_PRECISION} = Util;
export const PREVIEW_NUMBERS = [Math.PI, 0.003, 5.999, -173.2];

interface PrecisionInnerModalProps {
  precisionLink: {
    value: Optional<number>;
    maxPrecision?: Optional<number>;
    requestChange: ((precision: Optional<number>) => void) | undefined;
  };
  onRequestClose: () => void;
}

interface State {
  precision: Optional<number>;
  precisionString: string | number | undefined;
  valid: boolean;
}

export class PrecisionInnerModal extends React.Component<PrecisionInnerModalProps, State> {
  static propTypes = {
    precisionLink: PropTypes.shape({
      value: PropTypes.number,
      maxPrecision: PropTypes.number,
      requestChange: PropTypes.func,
    }).isRequired,
    onRequestClose: PropTypes.func.isRequired,
  };

  state: State = {
    precision: null,
    precisionString: undefined,
    valid: false,
  };

  _stateFromProps(props: PrecisionInnerModalProps): State {
    return {
      precision: props.precisionLink.value,
      precisionString: props.precisionLink.value ?? '',
      valid: props.precisionLink.value != null,
    };
  }

  constructor(props: PrecisionInnerModalProps) {
    super(props);
    this.state = this._stateFromProps(props);
  }

  UNSAFE_componentWillReceiveProps(newProps: PrecisionInnerModalProps): void {
    if (newProps !== this.props) {
      this.setState(this._stateFromProps(newProps));
    }
  }

  parsePrecision(precisionString: string): number | null {
    const precision = parseInteger(precisionString);
    if (precision != null && MIN_PRECISION <= precision && precision <= MAX_PRECISION) {
      return precision;
    }
    return null;
  }

  clearPrecisionAndClose = (): void => {
    this.props.precisionLink.requestChange!(null);
    this.props.onRequestClose();
  };

  setPrecisionAndClose = (): void => {
    this.props.precisionLink.requestChange!(this.state.precision);
    this.props.onRequestClose();
  };

  precisionInputChangeHandler = (e: InputChangeEvent): void => {
    const precisionString = e.target.value;
    const precision = this.parsePrecision(e.target.value);
    if (precision != null) {
      this.setState({
        precision,
        precisionString,
        valid: true,
      });
    } else {
      this.setState({
        precisionString,
        valid: false,
      });
    }
  };

  render(): JSX.Element {
    const isLocked = this.props.precisionLink.requestChange == null;
    const previewPrecision = this.state.precision != null ? this.state.precision : 3;
    // Only show the error after the user has entered invalid text (precisionString is defined)
    const showError = !this.state.valid && this.state.precisionString != null;
    const maxPrecision = this.props.precisionLink.maxPrecision || MAX_PRECISION;

    return (
      <Modal
        title="Display Precision"
        onRequestClose={this.props.onRequestClose}
        onRequestSubmit={!isLocked ? this.setPrecisionAndClose : undefined}
        buttons={
          isLocked
            ? [{text: 'Close', cancel: true}]
            : [
                {text: 'Set', submit: true, disabled: !this.state.valid},
                {
                  text: 'Clear',
                  onClick: this.clearPrecisionAndClose,
                  style: ButtonStyle.NEGATIVE,
                  pullLeft: true,
                },
              ]
        }
      >
        <div>
          <FormGroup hasError={showError}>
            <Label htmlFor="precision-input">{`Number of decimal places (${MIN_PRECISION}\u2013${maxPrecision})`}</Label>
            <FormInput
              id="precision-input"
              focusOnMount={true}
              type="number"
              min={MIN_PRECISION}
              max={maxPrecision}
              value={this.state.precisionString}
              onChange={this.precisionInputChangeHandler}
              disabled={isLocked}
            />
          </FormGroup>
          <div>
            <label>{`Examples (${previewPrecision} decimal ${pluralize(previewPrecision, 'place')})`}</label>
            <ul>
              {Array.from(PREVIEW_NUMBERS).map((number, i) => (
                <li key={i}>{`${number}  \u21d2   ${number.toFixed(previewPrecision)}`}</li>
              ))}
            </ul>
          </div>
        </div>
      </Modal>
    );
  }
}

interface SetPrecisionModalProps {
  open: boolean;
  onRequestClose: () => void;
  precisionLink: {
    value: Optional<number>;
    maxPrecision?: Optional<number>;
    requestChange: ((precision: Optional<number>) => void) | undefined;
  };
}

const SetPrecisionModal: React.SFC<SetPrecisionModalProps> = (props: SetPrecisionModalProps): JSX.Element => (
  <ModalAnchor open={props.open}>
    <PrecisionInnerModal precisionLink={props.precisionLink} onRequestClose={props.onRequestClose} />
  </ModalAnchor>
);

SetPrecisionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  precisionLink: PropTypes.object.isRequired,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any;

export default SetPrecisionModal;
